import React, { useEffect } from "react";

import { AccountBubbleDriftChevron } from "./AccountBubbleDriftChevron";
import { useAccountTiers } from "../../../Utils/Helper/accountTierHelpers";
import {
  formatAggregateRl,
  formatLevel,
  getAccountMappingDisplay,
  getPrototypeAlBubbleClass,
  getInitials,
  unassessedLevelTitle,
} from "../account-relationships/relationshipUtils";

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {object|null} props.account
 * @param {boolean} [props.showMyRelationships]
 * @param {() => void} props.onClose
 * @param {() => void} props.onGoAccountRelationships
 * @param {() => void} [props.onGoMyRelationships]
 */
export function PortfolioAccountPreviewModal({
  open,
  account,
  showMyRelationships = false,
  onClose,
  onGoAccountRelationships,
  onGoMyRelationships,
}) {
  const { formatTier } = useAccountTiers();

  useEffect(() => {
    if (!open) return undefined;
    function onKey(event) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !account) return null;

  const alLabel =
    account.alLevel === null || account.alLevel === undefined
      ? "AL ?"
      : formatLevel("AL", account.alLevel);
  const rlLabel =
    account.rlAggregate === null || account.rlAggregate === undefined
      ? "RL ?"
      : formatAggregateRl(account.rlAggregate);
  const mapping = getAccountMappingDisplay(account);

  return (
    <div
      id="modal-preview"
      role="presentation"
      className="modal-bg open"
      onClick={event => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <article
        className="modal"
        role="dialog"
        aria-labelledby="portfolio-preview-title"
        onClick={e => e.stopPropagation()}
      >
        <header className="modal-hdr">
          <div
            className={`acct-bubble ${getPrototypeAlBubbleClass(account.alLevel)}`}
            style={{
              width: 44,
              height: 44,
              flexShrink: 0,
              cursor: "default",
              borderWidth: 2,
            }}
            aria-hidden
          >
            <span className="ab-init" style={{ fontSize: 13 }}>
              {getInitials(account.name)}
            </span>
            <AccountBubbleDriftChevron rlAggregate={account.rlAggregate} />
          </div>
          <div className="mh-info">
            <h2 id="portfolio-preview-title" className="modal-title">
              {account.name}
            </h2>
            <div className="modal-sub">
              {[
                account.industry?.trim() || null,
                formatTier(account.tier) || account.tier || "Portfolio",
                account.region || null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </div>
          </div>
          <button
            type="button"
            className="modal-close"
            aria-label="Close preview"
            onClick={onClose}
          >
            ×
          </button>
        </header>

        <div className="modal-body">
          <div className="prev-stats">
            <div className="prev-stat">
              <div className="prev-stat-lbl">Published AL</div>
              <div
                className="prev-stat-val"
                title={unassessedLevelTitle(alLabel)}
              >
                {alLabel}
              </div>
              <div className="prev-stat-sub">From Account Level</div>
            </div>
            <div className="prev-stat">
              <div className="prev-stat-lbl">Aggregate RL</div>
              <div
                className="prev-stat-val"
                title={unassessedLevelTitle(rlLabel)}
              >
                {rlLabel}
              </div>
              <div className="prev-stat-sub">Team lens (hybrid aggregate)</div>
            </div>
            <div className="prev-stat">
              <div className="prev-stat-lbl">Mapping</div>
              <div className="prev-stat-val">{mapping.ratioLabel}</div>
              <div className="prev-stat-sub">{mapping.subLabel}</div>
            </div>
            <div className="prev-stat">
              <div className="prev-stat-lbl">Pending RL</div>
              <div className="prev-stat-val">
                {account.pendingAssessments ?? "—"}
              </div>
              <div className="prev-stat-sub">Pairs to assess</div>
            </div>
          </div>

          <div className="prev-actions">
            <button
              type="button"
              className="btn btn-pri btn-full"
              onClick={onGoAccountRelationships}
            >
              Open Account Relationships →
            </button>
            {showMyRelationships ? (
              <button
                type="button"
                className="btn btn-sec btn-full"
                onClick={onGoMyRelationships}
              >
                Open My Relationships (personal view) →
              </button>
            ) : null}
          </div>
        </div>
      </article>
    </div>
  );
}

export default PortfolioAccountPreviewModal;
